import type { ExportColumn } from '@/components/ui'
import type { PricelistItem, PricelistSummary } from '../api/types'

/**
 * CSV-export columns for the Customer Pricelists module. Stable keys —
 * the user's saved templates (in localStorage under
 * `spi-ops:customer-pricelists:export-templates`) reference these by
 * key. NEVER rename — old templates would orphan.
 *
 * Export source is the fixed-price items (`compute_price === 'fixed'`)
 * only. Non-fixed rules (percentage / formula) are read-only on screen
 * and intentionally excluded — there's no useful unit price to dump.
 */

function _m2oName(v: unknown): string {
  if (Array.isArray(v) && v.length >= 2) return String(v[1] ?? '')
  return ''
}

/**
 * SPI's customer pricelist items are almost entirely template-scoped
 * (`applied_on: '1_product'`) — `product_id` comes back `False` from
 * Odoo and only `product_tmpl_id` carries the name. Mirrors the same
 * variant-then-template fallback already used on-screen in
 * `PricelistEditor.tsx` / `PricelistDetail.tsx`.
 */
function _productName(r: PricelistItem): string {
  return _m2oName(r.product_id) || _m2oName(r.product_tmpl_id)
}

function _date(v: string | false | null | undefined): string {
  if (!v) return ''
  return String(v).slice(0, 10)
}

function _price(v: number | false | null | undefined): string {
  if (v === false || v === null || v === undefined) return ''
  return Number(v).toFixed(2)
}

/**
 * Forces Excel to treat a value as literal text via the `="…"` formula
 * idiom, so alphanumeric codes like `10-23` aren't auto-reinterpreted
 * as a date on CSV open. Only used for customer_part_number — applying
 * it to numeric columns would block the currency reformatting workflow
 * users already do in Excel after export.
 */
function _forceText(s: string): string {
  if (!s) return ''
  return `="${s.replace(/"/g, '""')}"`
}

function _marginPct(row: PricelistItem): string {
  const price = Number(row.fixed_price)
  const cost = row.last_paid_price
  if (!Number.isFinite(price) || price <= 0) return ''
  if (cost == null || !Number.isFinite(cost) || cost <= 0) return ''
  return (((price - cost) / price) * 100).toFixed(1)
}

export const COLUMNS: readonly ExportColumn<PricelistItem>[] = [
  { key: 'product_id', header: 'Product', value: (r) => _productName(r) },
  { key: 'customer_part_number', header: 'Customer Part #', value: (r) => _forceText(r.customer_part_number || '') },
  { key: 'min_quantity', header: 'Min Qty', value: (r) => r.min_quantity ?? '' },
  { key: 'fixed_price', header: 'Price', value: (r) => _price(r.fixed_price) },
  { key: 'last_paid_price', header: 'Our Cost', value: (r) => _price(r.last_paid_price) },
  { key: 'last_paid_uom_name', header: 'Our Cost UoM', value: (r) => r.last_paid_uom_name || '' },
  { key: 'last_paid_at', header: 'Our Cost On', value: (r) => _date(r.last_paid_at) },
  { key: 'margin_pct', header: 'Margin %', value: (r) => _marginPct(r) },
  { key: 'date_start', header: 'Valid From', value: (r) => _date(r.date_start) },
  { key: 'date_end', header: 'Valid To', value: (r) => _date(r.date_end) },
  { key: 'manufacturer_trade_name', header: 'Manufacturer Trade Name', value: (r) => r.manufacturer_trade_name || '' },
  { key: 'manufacturer_id', header: 'Manufacturer', value: (r) => _m2oName(r.manufacturer_id) },
  { key: 'country_of_origin_id', header: 'Country of Origin', value: (r) => _m2oName(r.country_of_origin_id) },
  { key: 'approved_vendors', header: 'Approved Vendors', value: (r) => (r.approved_vendors ?? []).map((v) => v.name).join('; ') },
] as const

export const ALL_COLUMN_KEYS: readonly string[] = COLUMNS.map((c) => c.key)

/**
 * Display name for a pricelist row where a definite (non-blank) name is
 * required — falls back to the pricelist's own name when no partner is
 * resolved. Shared by the bulk-export table's Customer column and the
 * bulk CSV row merge so both use the identical derivation.
 */
export function resolveCustomerName(pricelist: PricelistSummary): string {
  const partner = pricelist.partner_id
  const partnerName = partner ? partner[1] : null
  return partnerName ?? pricelist.name
}

/**
 * Bulk-export row shape — one customer's pricelist item, tagged with the
 * customer name so multiple customers' items can share one CSV. Leading
 * `__` avoids colliding with any real Odoo field name.
 */
export interface BulkPricelistItem extends PricelistItem {
  __customerName: string
}

export const BULK_COLUMNS: readonly ExportColumn<BulkPricelistItem>[] = [
  { key: '__customerName', header: 'Customer', value: (r) => r.__customerName },
  ...COLUMNS,
]

/**
 * URL/filename-safe slug for a pricelist name. Used to build a per-
 * pricelist CSV filename like `pricelist-acme-inc-2026-05-14.csv`.
 */
export function slugForFilename(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50) || 'untitled'
  )
}
